import type { CalendarRecord } from "@qendo/calendar";
import { router } from "expo-router";
import { useContext, useEffect, useMemo, useState } from "react";
import {
    FlatList,
    type NativeScrollEvent,
    type NativeSyntheticEvent,
    ScrollView,
    StyleSheet,
    Text,
    View,
} from "react-native";
import { CalendarContext } from "react-native-calendars";
import { Button } from "~/components/Button";
import { CycleTracker } from "~/components/form/journal/CycleTracker";
import { BackgroundGradient } from "~/components/layout/BackgroundGradient";
import { Columns } from "~/components/layout/Columns";
import { CalendarGroup } from "~/components/layout/calendar/CalendarGroup";
import { WeekDayCalendar } from "~/components/layout/calendar/WeekDayCalendar";
import { HomeCard } from "~/components/layout/home/HomeCard";
import { OnboardingCard } from "~/components/layout/home/OnboardingCard";
import { Page } from "~/components/layout/Page";
import { MINI_REPORT_HEIGHT, MiniReport } from "~/components/layout/reports/MiniReport";
import { LatestBlogs } from "~/components/layout/resources/LatestBlogs";
import { Colour, Title } from "~/components/layout/text";
import { Can } from "~/components/permission/Can";
import { CanAny } from "~/components/permission/CanAny";
import { Flex, Padding, Width } from "~/constants/layout";
import { Radius, Space } from "~/constants/sizing";
import { useJournal } from "~/providers/JournalProvider";
import { useNotifications } from "~/providers/NotificationsProvider";
import { useOnboarding } from "~/providers/OnboardingProvider";
import { useProfile } from "~/providers/ProfileProvider";
import { useReport } from "~/providers/ReportProvider";
import { useSurvey } from "~/providers/SurveyProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { useUser } from "~/providers/UserProvider";
import { filled } from "~/util";

export default function Dashboard() {
    const { user } = useUser();
    const { visible } = useOnboarding();
    const { profile } = useProfile();
    const { journalsForDay, eventsForDay } = useJournal();
    const { notifications, hasUnseen } = useNotifications();
    const { date } = useContext(CalendarContext);
    const { surveys } = useSurvey();
    const [cycleHidden, setCycleHidden] = useState(false);
    const { colours, background, border } = useTheme();
    const { reports } = useReport();
    const [animating, setAnimating] = useState(false);
    const [offset, setOffset] = useState(0);

    const handleScroll = ({ nativeEvent }: NativeSyntheticEvent<NativeScrollEvent>) => {
        const current_offset = nativeEvent.contentOffset.y;
        const down = current_offset > offset;
        setOffset(current_offset);
        const should_be_hidden = current_offset >= 5 && (cycleHidden || down);

        if (animating) {
            return;
        }
        if (should_be_hidden !== cycleHidden) {
            setAnimating(true);
            setCycleHidden(should_be_hidden);

            setTimeout(() => setAnimating(false), 300);
        }
    };

    const name = useMemo(() => (profile?.name ?? user?.name ?? "").split(" ")[0] ?? "Qendo", [profile, user]);

    const calendar = useMemo<CalendarRecord>(
        () => ({ [date]: [...eventsForDay(date).slice(0, 2), ...journalsForDay(date).slice(0, 4)] }),
        [eventsForDay, journalsForDay, date],
    );

    const onboard_cycle = visible("cycle");

    useEffect(() => {
        if (surveys.length) {
            setTimeout(() => router.navigate({ pathname: "/survey/[id]", params: { id: surveys[0].id } }), 10);
        } else if (onboard_cycle) {
            setTimeout(() => router.navigate("/on-boarding"), 10);
        }
    }, [onboard_cycle, surveys]);

    return (
        <Page gradient tabs padding={0} gap={0} scrollable={false}>
            {surveys.length > 0 && (
                <Button
                    icon="heart"
                    variant="primary"
                    size="lg"
                    title={surveys[0].title}
                    href={{ pathname: "/survey/[id]", params: { id: surveys[0].id } }}
                    style={{ borderTopRightRadius: 0, borderTopLeftRadius: 0 }}
                />
            )}
            <View style={styles.header}>
                <View style={styles.topRow}>
                    <Title size="lg" style={Flex.grow}>
                        Welcome back, {name}
                    </Title>
                    <Button
                        icon="ambulance"
                        variant="neutral-quiet"
                        colour={colours.icon.secondary}
                        size="sm"
                        iconSize={28}
                        style={Padding.xxs}
                        href="/emergency-information"
                    />
                    <Button
                        icon={notifications.length ? "bell-notification" : "bell"}
                        variant="neutral-quiet"
                        colour={hasUnseen ? colours.icon.selected : colours.icon.secondary}
                        href="/notifications"
                        size="sm"
                        iconSize={28}
                        style={Padding.xxs}
                    />
                </View>
                <CycleTracker forDate={date} hidden={cycleHidden} />
                <WeekDayCalendar />
            </View>
            <View style={[styles.bodySpacer, background.secondary, border.brand]} />
            <View style={styles.bodyContainer}>
                <BackgroundGradient
                    style={styles.bodyGradient}
                    colors={[colours.border.brand, colours.background.secondary]}
                />
                <ScrollView
                    style={[styles.body, background.secondary]}
                    contentContainerStyle={styles.scrollBody}
                    endFillColor={colours.background.secondary}
                    onScroll={handleScroll}
                    scrollEventThrottle={50}
                >
                    <View style={styles.innerBody}>
                        <CanAny section={["activities", "journal", "symptoms"]} ability="update">
                            <Columns
                                gap={Space.md}
                                items={[
                                    () => (
                                        <Button
                                            icon="plus"
                                            variant="primary"
                                            title="Add log"
                                            href={{ pathname: "/journal/add", params: { date } }}
                                        />
                                    ),
                                    () => (
                                        <Button
                                            icon="zap"
                                            variant="destructive"
                                            title="Log flare"
                                            href="/journal/flare"
                                        />
                                    ),
                                ]}
                            />
                        </CanAny>
                        <CanAny section={["activities", "journal", "symptoms", "calendar"]} ability="view">
                            <CalendarGroup calendar={calendar} asStatic />
                            <Button href="/calendar" title="All logs" variant="secondary" size="lg" />
                            <CanAny section={["activities", "symptoms"]} ability="view">
                                <HomeCard
                                    title="Reports"
                                    icon="report"
                                    subtitle="Reports of your logged symptoms and activities. You can share with your care team."
                                    href="/more/reports"
                                    linkText="All reports"
                                >
                                    {filled(reports) && (
                                        <View style={{ height: MINI_REPORT_HEIGHT }}>
                                            <FlatList
                                                scrollEventThrottle={50}
                                                horizontal
                                                data={reports}
                                                ItemSeparatorComponent={() => <View style={Width.md} />}
                                                renderItem={({ item }) => <MiniReport report={item} />}
                                            />
                                        </View>
                                    )}
                                </HomeCard>
                            </CanAny>
                        </CanAny>
                        <Can section="team" ability="view">
                            <OnboardingCard
                                title={
                                    <Text>
                                        Invite your <Colour colour="brand">care team</Colour>
                                    </Text>
                                }
                                icon="care-team"
                                subtitle="Add people to your care team, like family and healthcare providers, to make sharing information and health updates easy."
                                button="/more/care-team"
                                buttonText="Care team"
                                section="care_team"
                            />
                        </Can>
                        <OnboardingCard
                            title={
                                <Text>
                                    Add to your <Colour colour="brand">health profile</Colour>
                                </Text>
                            }
                            icon="health-profile"
                            subtitle="Organise your health information in one place and easily share key details with your care team."
                            button="/health-profile"
                            buttonText="Health Profile"
                            section="health_profile"
                        />
                        <HomeCard
                            title="Helpful resources"
                            icon="resources"
                            subtitle="Our curated educational resources are designed to inform and support you."
                            href="/resources"
                            linkText="All resources"
                        >
                            <LatestBlogs />
                        </HomeCard>
                    </View>
                </ScrollView>
            </View>
        </Page>
    );
}

const styles = StyleSheet.create({
    header: {
        gap: Space.xs,
        paddingTop: Space.md,
        position: "relative",
    },
    topRow: {
        flexDirection: "row",
        paddingHorizontal: Space.lg,
        width: "100%",
        alignItems: "center",
        gap: Space.xs,
    },
    body: {
        flex: 1,
        gap: Space.lg,
        marginLeft: 4,
        marginRight: 4,
        borderColor: "transparent",
        position: "relative",
    },
    scrollBody: {
        marginTop: -12,
    },
    bodyGradient: {
        maxHeight: 200,
    },
    bodyContainer: {
        flex: 1,
        position: "relative",
    },
    bodySpacer: {
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        height: 16,
        borderTopWidth: 4,
        borderLeftWidth: 4,
        borderRightWidth: 4,
        marginTop: -8,
    },
    innerBody: {
        width: "100%",
        padding: Space.lg,
        gap: Space.xl,
        borderTopLeftRadius: Radius.xxl,
        borderTopRightRadius: Radius.xxl,
        flex: 1,
    },
});
